// Global Instructions Rule Applied!

import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  JournalEntryRecord,
  validateJournalEntryRecord
} from '../../../state_manager/data_models/journal_entry_record.ts'
import {
  JournalEntryDetail,
  JournalEntryOptions,
  JournalEntrySavePayload,
  validateJournalEntryDetail
} from '../../../state_manager/data_models/journal_entry_detail.ts'
import { FilterValue, SortConfig } from '../../global_configs/tableViewPreferences.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

export interface JournalEntriesPaginatedResponse {
  data: JournalEntryRecord[]
  hasMore: boolean
  totalCount: number
  error?: string
}

export interface JournalEntryDetailResponse {
  data: JournalEntryDetail | null
  error?: string
}

export interface JournalEntryActionResponse {
  data: Record<string, any> | null
  error?: string
}

function authHeaders(accessToken: string | null): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

export const fetchJournalEntriesFiltered = async (
  sorting: SortConfig[] = [],
  filters: FilterValue[] = [],
  page: number = 1,
  pageSize: number | null = null,
  accessToken: string | null = null
): Promise<JournalEntriesPaginatedResponse> => {
  try {
    const requestBody = {
      data: {
        page,
        page_size: pageSize ?? 200,
        sorting: sorting.map((s) => ({ key: s.key, direction: s.direction })),
        filters: filters.map((f) => ({ key: f.key, value: f.value }))
      }
    }
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.JOURNAL_ENTRIES.FILTER), {
      method: API_ROUTES.JOURNAL_ENTRIES.FILTER.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify(requestBody)
    })
    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch journal entries: ${response.status} ${response.statusText}`)
    }
    const json = (await response.json()) as { data?: unknown[]; has_more?: boolean; count?: number }
    return {
      data: validateJournalEntryRecord(Array.isArray(json.data) ? json.data : [], true),
      hasMore: Boolean(json.has_more),
      totalCount: Number(json.count ?? 0)
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching journal entries:', error)
    return {
      data: [],
      hasMore: false,
      totalCount: 0,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export const fetchJournalEntriesFilterOptions = async (
  accessToken: string | null = null
): Promise<{ data: Record<string, Array<{ value: string; label: string }>>; error?: string }> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.JOURNAL_ENTRIES.FILTER_OPTIONS), {
      method: 'GET',
      headers: authHeaders(accessToken)
    })
    if (!response.ok) throw new Error(`Failed to fetch filter options: ${response.status}`)
    const json = (await response.json()) as { data?: Record<string, Array<{ value: string; label: string }>> }
    return { data: json.data ?? {} }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching JE filter options:', error)
    return { data: {}, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchJournalEntryDetail = async (
  entryId: string,
  accessToken: string | null = null
): Promise<JournalEntryDetailResponse> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.JOURNAL_ENTRIES.DETAIL, { id: entryId }, {})
    const response = await fetch(url, { method: API_ROUTES.JOURNAL_ENTRIES.DETAIL.method, headers: authHeaders(accessToken) })
    if (!response.ok) {
      if (response.status === 404) return { data: null, error: 'Journal entry not found' }
      throw new Error(`Failed to fetch journal entry: ${response.status}`)
    }
    const json = (await response.json()) as { data?: unknown }
    return { data: validateJournalEntryDetail(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching journal entry detail:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchJournalEntryOptions = async (
  fundId: string,
  accessToken: string | null = null
): Promise<{ data: JournalEntryOptions; error?: string }> => {
  const empty: JournalEntryOptions = { lps: [], fund_transactions: [], asset_transactions: [] }
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.JOURNAL_ENTRIES.OPTIONS, {}, { fund_id: fundId })
    const response = await fetch(url, { method: API_ROUTES.JOURNAL_ENTRIES.OPTIONS.method, headers: authHeaders(accessToken) })
    if (!response.ok) throw new Error(`Failed to fetch journal entry options: ${response.status}`)
    const json = (await response.json()) as { data?: Partial<JournalEntryOptions> }
    return {
      data: {
        lps: json.data?.lps ?? [],
        fund_transactions: json.data?.fund_transactions ?? [],
        asset_transactions: json.data?.asset_transactions ?? []
      }
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching journal entry options:', error)
    return { data: empty, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const saveJournalEntry = async (
  payload: JournalEntrySavePayload,
  accessToken: string | null = null
): Promise<JournalEntryDetailResponse> => {
  try {
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.JOURNAL_ENTRIES.SAVE), {
      method: API_ROUTES.JOURNAL_ENTRIES.SAVE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: payload })
    })
    const json = (await response.json()) as { data?: unknown; error?: string; message?: string }
    if (!response.ok) {
      return { data: null, error: json.error || (json as any).message || `Failed to save journal entry: ${response.status}` }
    }
    return { data: validateJournalEntryDetail(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error saving journal entry:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

const entryAction = async (
  route: { path: string; method: string },
  entryId: string,
  accessToken: string | null
): Promise<JournalEntryActionResponse> => {
  try {
    const url = buildFullUrl(BACKEND_BASE_URL, route as any, { id: entryId }, {})
    const response = await fetch(url, { method: route.method, headers: authHeaders(accessToken) })
    const json = (await response.json()) as { data?: Record<string, any>; error?: string }
    if (!response.ok) {
      return { data: json.data ?? null, error: json.error || `Request failed: ${response.status}` }
    }
    return { data: json.data ?? {} }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error running journal entry action:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const bookJournalEntry = (entryId: string, accessToken: string | null = null) =>
  entryAction(API_ROUTES.JOURNAL_ENTRIES.BOOK, entryId, accessToken)

export const unbookJournalEntry = (entryId: string, accessToken: string | null = null) =>
  entryAction(API_ROUTES.JOURNAL_ENTRIES.UNBOOK, entryId, accessToken)

export const trashJournalEntry = (entryId: string, accessToken: string | null = null) =>
  entryAction(API_ROUTES.JOURNAL_ENTRIES.TRASH, entryId, accessToken)
